import { ColumnDef } from "@tanstack/react-table";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { ArrowUpDown, Trash } from "lucide-react";
import TableWrapper from "../TableWrapper";
import DeleteDepartmentModal from "../../modal/DeleteDepartmentModal";
import { useTranslation } from "react-i18next";
import { TABLE_IDS } from "../../../constants/common";

interface Department {
  n_DepartmentID: number;
  s_DepartmentName_En: string;
  s_DepartmentName_Ar: string;
  s_Description: string;
  s_LocationName_En: string;
  s_LocationName_Ar: string;
  s_BuildingName_En?: string;
  s_BuildingName_Ar?: string;
}

interface DepartmentsTableProps {
  departments: Department[];
  setSelectedDepartmentId: Dispatch<SetStateAction<number>>;
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const DepartmentsTable = ({
  departments,
  setSelectedDepartmentId,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  pageSize,
  setPageSize,
}: DepartmentsTableProps) => {
  const [isDeleteDepartmentModalOpen, setIsDeleteDepartmentModalOpen] =
    useState(false);
  const [selectedDeleteDepartmentId, setSelectedDeleteDepartmentId] =
    useState(0);

  const { t, i18n } = useTranslation();

  const handleDeleteDepartment = useCallback(async (locationId: number) => {
    setIsDeleteDepartmentModalOpen(true);
    setSelectedDeleteDepartmentId(locationId);
  }, []);

  const columns: ColumnDef<Department>[] = useMemo(
    () => [
      {
        accessorKey: "n_DepartmentID",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("departmentId")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span
              onClick={() =>
                setSelectedDepartmentId(row.original.n_DepartmentID)
              }
              className="text-blue-800 underline cursor-pointer capitalize"
            >
              {row.original.n_DepartmentID}
            </span>
          );
        },
      },
      {
        accessorKey: "s_DepartmentName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("departmentNameEn")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_DepartmentName_En}</span>;
        },
      },
      {
        accessorKey: "s_DepartmentName_Ar",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("departmentNameAr")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_DepartmentName_Ar}</span>;
        },
      },
      {
        accessorKey: "s_Description",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("description")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_Description}</span>;
        },
      },
      {
        accessorKey: "s_LocationName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("location")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_LocationName_En
                : row.original.s_LocationName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_BuildingName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("building")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_BuildingName_En
                : row.original.s_BuildingName_Ar}
            </span>
          );
        },
      },
      {
        header: t("action"),
        cell: ({ row }) => {
          return (
            <Trash
              onClick={() =>
                handleDeleteDepartment(row.original.n_DepartmentID)
              }
              className="w-5 h-5 text-red-600 cursor-pointer"
            />
          );
        },
      },
    ],
    [handleDeleteDepartment, i18n.language, setSelectedDepartmentId, t]
  );

  return (
    <>
      <TableWrapper
        data={departments}
        columns={columns}
        isColumnChooser={false}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isSearch
        tableId={TABLE_IDS.DEPARTMENTS}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <DeleteDepartmentModal
        departmentId={selectedDeleteDepartmentId}
        isOpen={isDeleteDepartmentModalOpen}
        onOpenChange={() => setIsDeleteDepartmentModalOpen(false)}
      />
    </>
  );
};

export default React.memo(DepartmentsTable);
